/**
 * Crosshair readout — the one place a renderer inverts a pointer position
 * back into data, for EVERY chart.
 *
 * A drawer describes what it painted as a `PlotDescriptor` (the plot box plus
 * the x and y scales it laid the data out with); the chart shell reads that
 * description back through `readCrosshair` to place the two cursor lines and
 * the axis-value boxes under them (the REW graph-cursor idiom: a vertical
 * line and a horizontal line meeting under the pointer, each labelled with
 * its value on its own axis). Because the descriptor comes from the SAME
 * scales the curve was drawn with, the readout cannot drift from the drawing
 * — AGENTS.md's "scrubbers invert via the scale that drew" rule, made
 * structural instead of a convention each chart had to re-honour by hand.
 *
 * Pure: no DOM, no canvas. Web's Chart shell and a future Skia cursor read the
 * same description.
 */

import type { PlotFrame } from './layout';
import {
  linearToPlot,
  logFreqToX,
  plotToLinear,
  plotToLogFreq,
  type LinearScale,
  type LogFreqScale,
} from './scales';

export type AxisScale = LogFreqScale | LinearScale;

export interface PlotAxis {
  /** The scale the drawer mapped data through — plot-local px on one side. */
  scale: AxisScale;
  /** Readout formatter; defaults to `formatAxisValue`'s rules. */
  format?: (v: number) => string;
}

export interface PlotDescriptor {
  frame: PlotFrame;
  x: PlotAxis;
  /** Absent when the chart has no readable y axis (a waterfall cascade's y is
   *  time AND level; a spectrogram's rows are categorical bands). The cursor
   *  then draws its vertical line only. */
  y?: PlotAxis;
}

export interface CrosshairReading {
  /** View-space px (origin = host top-left), clamped inside the plot box. */
  px: number;
  py: number;
  /** Data value under the vertical line. */
  x: number;
  /** Data value under the horizontal line; null when the chart has no y axis. */
  y: number | null;
  xLabel: string;
  yLabel: string | null;
}

export interface AxisMarker {
  /** View-space px of the value on the x axis. */
  px: number;
  label: string;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function invert(scale: AxisScale, p: number): number {
  return scale.kind === 'log-freq' ? plotToLogFreq(scale, p) : plotToLinear(scale, p);
}

function inRange(scale: AxisScale, v: number): boolean {
  if (scale.kind === 'log-freq') return v >= scale.fMin && v <= scale.fMax;
  const lo = Math.min(scale.lo, scale.hi);
  const hi = Math.max(scale.lo, scale.hi);
  return v >= lo && v <= hi;
}

/** Frequency the way a graph cursor reports it: three significant figures
 *  above 1 kHz ("1.23k", "12.3k"), whole hertz in the hundreds, one decimal
 *  below that ("53.9"). The unit is on the axis title, not repeated here. */
export function formatFreqReadout(hz: number): string {
  if (hz >= 10000) return `${(hz / 1000).toFixed(1)}k`;
  if (hz >= 1000) return `${(hz / 1000).toFixed(2)}k`;
  if (hz >= 100) return `${Math.round(hz)}`;
  return hz.toFixed(1);
}

/** Linear readout precision follows the axis span, so a ±1 amplitude axis
 *  reads "0.42" while a 0–150 ms axis reads "37". */
export function formatLinearReadout(v: number, span: number): string {
  const s = Math.abs(span);
  const decimals = s > 50 ? 0 : s > 5 ? 1 : 2;
  const text = v.toFixed(decimals);
  // "-0.0" is a rounding artefact, not a value.
  return /^-0(?:\.0+)?$/.test(text) ? text.slice(1) : text;
}

export function formatAxisValue(axis: PlotAxis, v: number): string {
  if (axis.format) return axis.format(v);
  if (axis.scale.kind === 'log-freq') return formatFreqReadout(v);
  return formatLinearReadout(v, axis.scale.hi - axis.scale.lo);
}

/**
 * Pointer position (view px, origin = host top-left) → the crosshair to draw.
 * Clamped to the plot box rather than rejected outside it: a finger dragged
 * past the axis keeps reading the edge value instead of losing the cursor.
 * Null only when the plot box has no area.
 */
export function readCrosshair(plot: PlotDescriptor, viewX: number, viewY: number): CrosshairReading | null {
  const { frame } = plot;
  if (frame.plotW <= 0 || frame.plotH <= 0) return null;
  const lx = clamp(viewX - frame.plotX, 0, frame.plotW);
  const ly = clamp(viewY - frame.plotY, 0, frame.plotH);
  const x = invert(plot.x.scale, lx);
  const y = plot.y ? invert(plot.y.scale, ly) : null;
  return {
    px: frame.plotX + lx,
    py: frame.plotY + ly,
    x,
    y,
    xLabel: formatAxisValue(plot.x, x),
    yLabel: plot.y && y != null ? formatAxisValue(plot.y, y) : null,
  };
}

/**
 * A standing marker on the x axis (the frequency the 3D field is showing, the
 * reflection-time filter…): where a data value sits, and its label. Null when
 * the value lies outside the axis, so the shell draws nothing rather than a
 * line pinned to the wrong edge.
 */
export function markerAtX(plot: PlotDescriptor, xValue: number): AxisMarker | null {
  const { frame } = plot;
  if (frame.plotW <= 0 || !Number.isFinite(xValue) || !inRange(plot.x.scale, xValue)) return null;
  const scale = plot.x.scale;
  const lx = scale.kind === 'log-freq' ? logFreqToX(scale, xValue) : linearToPlot(scale, xValue);
  return { px: frame.plotX + lx, label: formatAxisValue(plot.x, xValue) };
}
